import {
  ApiError,
  HttpError,
  ValidationError,
  baseError,
} from '@pdcfrontendui/staffplan';

import type { BiometricErrorEnum } from '@pdcshell/helpers/biometric/biometricTypes';

/**
 * An error occured during React rendering.
 * It was caught by an ErrorBoundary or the router provider.
 */
export class ReactError extends baseError('ReactError')<{
  type: 'app' | 'section' | 'element' | 'effect' | 'route';
}> {}

/**
 * A value was thrown that was not an instance of Error.
 */
export class ThrownNonError extends baseError('ThrownNonError')<{
  type:
    | 'string'
    | 'number'
    | 'bigint'
    | 'boolean'
    | 'symbol'
    | 'null'
    | 'undefined'
    | 'object'
    | 'function';
}> {}

export class BiometricError extends baseError('BiometricError')<{
  type: BiometricErrorEnum;
}> {}

export class OpenIdError extends baseError('OpenIdError')<{
  type:
    | 'missingCodeParameter'
    | 'externalWindowClosed'
    | 'safariViewControllerUnavailable'
    | 'openWindowFailed'
    | 'invalidResponse';
  /**
   * A message suitable for displaying to the user.
   * This is not set on "message" to avoid accidental leakage of sensitive info via logging.
   */
  userOnlyMessage?: string;
}> {}

export class CameraError extends baseError('CameraError')<{
  type: 'cannotStopVideoTracks' | 'deviceConstraintsNotSatisfied' | 'qrScanError';
}> {}
export class DeviceError extends baseError('DeviceError')<{
  type: 'playbackNotAllowed' | 'playbackNotSupported' | 'playbackFailed';
}> {}

/**
 * An error was thrown and not handled.
 * This is the most generic error type.
 */
class UnhandledRuntimeError extends baseError('UnhandledRuntimeError') {}

export type PdcError =
  | ApiError
  | HttpError
  | ValidationError
  | OpenIdError
  | ReactError
  | ThrownNonError
  | UnhandledRuntimeError
  | CameraError
  | DeviceError
  | BiometricError;

export function isPdcError(error: Error): error is PdcError {
  return (
    error instanceof ApiError ||
    error instanceof HttpError ||
    error instanceof ValidationError ||
    error instanceof OpenIdError ||
    error instanceof ReactError ||
    error instanceof ThrownNonError ||
    error instanceof UnhandledRuntimeError ||
    error instanceof DeviceError ||
    error instanceof BiometricError ||
    error instanceof CameraError
  );
}

export function coerceToError(e: unknown, messageIfUnknown?: string): PdcError {
  return e instanceof Error
    ? isPdcError(e)
      ? e
      : new UnhandledRuntimeError({ message: e.message })
    : new ThrownNonError({
        message: messageIfUnknown,
        type: e === null ? 'null' : typeof e,
      });
}
